import React, { FC, useMemo, useState, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import Skeleton from "@mui/material/Skeleton";
import { SxProps, Theme } from "@mui/material/styles";
import {
  DataGrid,
  GridColDef,
  gridClasses,
  GridSortModel,
  GridPaginationModel,
  GridRow,
  GridRowProps,
  GridRowParams,
} from "@mui/x-data-grid";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { useGetCompanyList } from "./hooks/useGetCompanyList";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import useMatchProductsById from "../MarketLandscape/hooks/useMatchProductsById";
import { NoData } from "../../ChartMessages/NoData";
import { lightScrollbarStyle } from "../../../../styles/lightScrollbarStyle";

import { formatNumber } from "../../../../utils/numberUtils";
import { TableRow, SkeletonRow, DataRow } from "./types";
import {
  getBaseColumns,
  getCompetitorPresenceColumn,
  getOpportunityTypeColumn,
} from "./columns";
import { CompanyDrawer } from "../../../Company/CompanyDrawer";
import { transformTechnographicFiltersToInstallFilters } from "./utils";
import Link from "@mui/material/Link";
import { LabelDisplayedRowsArgs } from "@mui/material/TablePagination";

// Custom row component that renders as an anchor tag
const LinkRow = React.forwardRef<HTMLAnchorElement, GridRowProps>(
  (props, ref) => {
    const { row } = props;

    if (row?.isSkeleton) {
      return <GridRow {...props} />;
    }

    const href = `/catalog/companies/${row?.hgId}`;

    return (
      <Link
        ref={ref}
        href={href}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        underline="none"
      >
        <GridRow {...props} />
      </Link>
    );
  }
);

LinkRow.displayName = "LinkRow";

const dataGridStyles: SxProps<Theme> = {
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
    {
      outline: "none",
    },
  [`& .${gridClasses.columnSeparator}`]: {
    [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
      display: "none",
    },
  },
  "& .MuiDataGrid-columnHeader": {
    borderBottom: "none !important",
  },
  "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
    "& .MuiIconButton-root": {
      padding: 0,
    },
    "& .MuiTouchRipple-root": {
      display: "none",
    },
  },
  "&, [class^=MuiDataGrid]": { border: "none" },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    borderBottom: "none",
  },
  "& .MuiDataGrid-filler": {
    display: "none",
  },
  ".MuiDataGrid-scrollbarFiller": {
    display: "none",
  },
  "& .MuiDataGrid-scrollbar--vertical": {
    ...lightScrollbarStyle,
  },
  "& .MuiDataGrid-scrollbar--horizontal": {
    ...lightScrollbarStyle,
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    color: "#3a3a3a",
    fontSize: "12px",
    fontWeight: "bold",
  },
};

const generateSkeletonRows = (count: number): SkeletonRow[] => {
  return Array.from({ length: count }, (_, index) => ({
    hgId: `skeleton-${index}`,
    isSkeleton: true,
  }));
};

export const CompanyList: FC = () => {
  const setCompanyCount = useWorkspaceStore((state) => state.setCompanyCount);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "revenue_range_min", sort: "desc" },
  ]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    companyId: string | null;
  }>({ open: false, companyId: null });

  const configuration = useWorkspaceStore(
    (state) => state.draft?.configuration
  );
  const combinedFilters = useCombinedFilters();

  const sortField = sortModel[0]?.field || "revenue_range_min";
  const sortDirection = sortModel[0]?.sort || "desc";

  const { data, loading, count } = useGetCompanyList({
    filters: combinedFilters,
    competitorPresence: configuration?.competitorsProductIds || [],
    companyMatchFile: configuration?.whitespace,
    localizeInstalls: configuration?.localizeInstalls ?? true,
    page: paginationModel.page,
    rowsPerPage: paginationModel.pageSize,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    setCompanyCount(count || 0);
  }, [count, setCompanyCount]);

  const { productsNameMapById } = useMatchProductsById(
    configuration?.competitorsProductIds || []
  );

  const hasCompetitorPresence =
    (configuration?.competitorsProductIds ?? []).length > 0;
  const hasMatchFile = !!configuration?.whitespace;
  const enableHierarchyClassification =
    configuration?.whitespace?.enableHierarchyClassification ?? false;

  const columns = useMemo(() => {
    const cols: GridColDef<TableRow>[] = [
      ...getBaseColumns(),
      getCompetitorPresenceColumn(productsNameMapById, hasCompetitorPresence),
      getOpportunityTypeColumn(hasMatchFile, enableHierarchyClassification),
    ];

    return cols;
  }, [
    hasCompetitorPresence,
    hasMatchFile,
    enableHierarchyClassification,
    productsNameMapById,
  ]);

  const rows: TableRow[] = useMemo(() => {
    if (loading) {
      return generateSkeletonRows(10);
    }
    return data.map((company) => ({
      ...company,
      isSkeleton: false,
    })) as DataRow[];
  }, [loading, data]);

  const companyIdsList = useMemo(() => {
    return rows.filter((row) => !row.isSkeleton).map((row) => row.hgId);
  }, [rows]);

  const handleSortModelChange = useCallback((newModel: GridSortModel) => {
    setSortModel(newModel);
  }, []);

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      setPaginationModel(newModel);
    },
    []
  );

  const handleRowClick = useCallback((params: GridRowParams) => {
    if (!params.row.isSkeleton) {
      setDrawerState({
        open: true,
        companyId: params.row.hgId,
      });
    }
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerState({ open: false, companyId: null });
  }, []);

  if (!combinedFilters) return null;

  const showEmptyState = !loading && rows.length === 0;

  return (
    <>
      <Box
        id="company-list-table"
        display="flex"
        flexDirection="column"
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
        width="100%"
      >
        {showEmptyState ? (
          <Box height={450}>
            <NoData />
          </Box>
        ) : (
          <>
            {loading ? (
              <Stack
                direction="row"
                alignItems="center"
                sx={{ ml: 1, height: 20 }}
              >
                <Skeleton
                  variant="text"
                  width={24}
                  height={20}
                  sx={{ display: "inline-block", mr: 1 }}
                />
                <Typography variant="h5">Organizations found</Typography>
              </Stack>
            ) : (
              <Typography variant="h5" sx={{ pl: 1, height: 20 }}>
                {`${formatNumber(count)} Organizations found`}
              </Typography>
            )}
            <Box sx={{ height: "calc(100vh - 280px)", width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="server"
                rowCount={count}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                sortingMode="server"
                sortModel={sortModel}
                sortingOrder={["asc", "desc"]}
                onSortModelChange={handleSortModelChange}
                getRowId={(row) => row.hgId}
                onRowClick={handleRowClick}
                disableColumnFilter
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnResize
                disableColumnSelector
                slots={{
                  row: LinkRow,
                }}
                slotProps={{
                  pagination: {
                    showFirstButton: true,
                    showLastButton: true,
                    labelDisplayedRows: ({
                      from,
                      to,
                      count,
                    }: LabelDisplayedRowsArgs) => {
                      return `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(count)}`;
                    },
                  },
                }}
                sx={dataGridStyles}
              />
            </Box>
          </>
        )}
      </Box>
      <CompanyDrawer
        open={drawerState.open}
        onClose={handleDrawerClose}
        companyId={drawerState.companyId}
        companyIds={companyIdsList}
        installFilters={transformTechnographicFiltersToInstallFilters(
          combinedFilters
        )}
      />
    </>
  );
};
